import React, { useEffect, useState, useCallback } from 'react';
import {
  Grid,
  Typography,
  Button,
  Box,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  MenuItem,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { SortableTable } from 'common/components/SortableTable';
import { getTokens } from './actions/getTokens';
import { getAdminCandidates } from './actions/getAdminCandidates';
import { revokeToken } from './actions/revokeToken';
import { reassignToken } from './actions/reassignToken';

// Item 3 — admin Tokens panel. Rows are a real read (GET /admin/tokens-list). Revoke /
// Reassign are live and only offered for UNSTARTED tokens (assignmentStatus === 1); booked/
// assessed tokens (2/3) show no actions. The backend authoritatively re-checks status,
// same-sponsor, and no-active-assignment, so any violation surfaces as an error toast.
const TABLE_HEADERS = [
  { id: 'id', align: 'left', numeric: false, label: 'TOKEN' },
  { id: 'type', align: 'left', numeric: false, label: 'TYPE' },
  { id: 'candidate', align: 'left', numeric: false, label: 'CANDIDATE' },
  { id: 'sponsor', align: 'left', numeric: false, label: 'SPONSOR' },
  { id: 'status', align: 'left', numeric: false, label: 'STATUS' },
  { id: 'actions', align: 'right', numeric: false, label: '' },
];

function createData(t, handlers) {
  const canAct = t.assignmentStatus === 1;
  return [
    { id: 'id', value: t.id },
    { id: 'type', value: t.type },
    { id: 'candidate', value: t.candidate },
    { id: 'sponsor', value: t.sponsor },
    { id: 'status', value: t.status },
    {
      id: 'actions',
      align: 'right',
      value: canAct ? (
        <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
          <Button size="small" variant="outlined" onClick={() => handlers.onReassign(t)}>
            Reassign
          </Button>
          <Button size="small" variant="outlined" color="secondary" onClick={() => handlers.onRevoke(t)}>
            Revoke
          </Button>
        </Box>
      ) : (
        ''
      ),
    },
  ];
}

const Tokens = () => {
  const [tokens, setTokens] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [toast, setToast] = useState({ msg: '', severity: 'success' });
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reassignTo, setReassignTo] = useState('');
  const [busy, setBusy] = useState(false);

  const notify = (msg, severity = 'success') => setToast({ msg, severity });

  const load = useCallback(() => {
    let mounted = true;
    Promise.all([getTokens(), getAdminCandidates()]).then(([t, c]) => {
      if (!mounted) return;
      setTokens(t);
      setCandidates(c);
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => load(), [load]);

  const handlers = {
    onRevoke: (t) => setRevokeTarget(t),
    onReassign: (t) => {
      setReassignTarget(t);
      setReassignTo('');
    },
  };

  const rows = tokens.map((t) => createData(t, handlers));

  // Reassign picker — candidates of the same sponsor as the token, excluding the current
  // holder. The backend still enforces same-sponsor / no-active-assignment authoritatively.
  const reassignOptions = reassignTarget
    ? candidates.filter(
        (c) => c.sponsor && c.sponsor === reassignTarget.sponsor && c.id !== reassignTarget.candidateId,
      )
    : [];

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setBusy(true);
    const result = await revokeToken(revokeTarget.id);
    setBusy(false);
    if (result.ok) {
      notify(`Token ${revokeTarget.id} revoked and returned to ${revokeTarget.sponsor}'s pool.`);
      setRevokeTarget(null);
      load();
    } else {
      notify(result.message, 'error');
      setRevokeTarget(null);
    }
  };

  const confirmReassign = async () => {
    if (!reassignTarget || !reassignTo) return;
    setBusy(true);
    const result = await reassignToken(reassignTarget.id, reassignTo);
    setBusy(false);
    if (result.ok) {
      const c = candidates.find((x) => x.id === reassignTo);
      notify(`Token ${reassignTarget.id} reassigned to ${c ? c.name : 'candidate'}.`);
      setReassignTarget(null);
      load();
    } else {
      notify(result.message, 'error');
      setReassignTarget(null);
    }
  };

  return (
    <Grid item xs={12}>
      <Typography variant="h5" style={{ marginTop: 16, marginBottom: 8 }}>
        Tokens
      </Typography>
      <SortableTable
        name="admin-tokens"
        headers={TABLE_HEADERS}
        rows={rows}
        defaultSortColumnIndex={0}
        searchable
        searchColumnName="candidate"
      />

      {/* Revoke confirm */}
      <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Revoke token?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {revokeTarget
              ? `Revoke ${revokeTarget.id} from ${revokeTarget.candidate}? It will return to the sponsor's pool.`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={confirmRevoke} variant="contained" color="secondary" disabled={busy}>
            {busy ? 'Revoking…' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reassign */}
      <Dialog open={!!reassignTarget} onClose={() => setReassignTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reassign token</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {reassignTarget ? `Move ${reassignTarget.id} to another candidate:` : ''}
          </DialogContentText>
          <TextField
            select
            label="Candidate"
            fullWidth
            value={reassignTo}
            onChange={(e) => setReassignTo(e.target.value)}
            style={{ marginTop: 8 }}
            helperText={reassignOptions.length ? '' : 'No eligible candidates for this sponsor'}
          >
            {reassignOptions.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name} ({c.email})
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReassignTarget(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={confirmReassign}
            variant="contained"
            color="primary"
            disabled={!reassignTo || busy}
          >
            {busy ? 'Reassigning…' : 'Reassign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast.msg}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, msg: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={toast.severity}
          onClose={() => setToast({ ...toast, msg: '' })}
        >
          {toast.msg}
        </MuiAlert>
      </Snackbar>
    </Grid>
  );
};

export default Tokens;
